import * as React from 'react';
import {
  Box,
  Grid,
  Tab,
} from '@material-ui/core';
import { Alert, TabContext, TabList } from '@material-ui/lab';
import type { AxiosError } from 'axios';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import useSWR from 'swr';
import { useSearchParams } from 'react-router-dom';
import type {
  ICreateRuleCommand, IMarkingDynamicBehavior, IRuleCondition, IRuleDetail, IRuleOutcome,
} from '../shared/Utilities/PolicyConfiguratorInterfaces';
import {
  emptyMarkingDynamicBehaviorCondition,
  companyRequiredWorkflowTypes,
  emptyRuleCondition,
  POLICYCONFIGURATORSECTIONS,
} from '../shared/Utilities/PolicyConfiguratorInterfaces';
import {
  emptyCompany, getEnumValues, getWorkflowFromPolicyConfiguratorSection, initialOutcomesForWorkflow,
} from '../shared/Utilities/PolicyConfiguratorHelpers';
import type { ICompany } from '../../massReceive/MassReceiveInterfaces';
import RuleFilters from '../shared/RuleFilters';
import UpsertRuleConfirmationDialog from './UpsertRuleConfirmationDialog';

interface IUpsertRuleProps {
  policyConfiguratorSection: POLICYCONFIGURATORSECTIONS;
  isEdit?: boolean;
}

const UpsertRule: React.FC<IUpsertRuleProps> = ({ policyConfiguratorSection, isEdit = false }) => {
  const [showUpsertRuleConfirmationDialog, setShowUpsertRuleConfirmationDialog] = React.useState(false);
  const [showErrors, setShowErrors] = React.useState(false);

  const [searchParams] = useSearchParams();
  const ruleNameParam = searchParams.get('ruleName');
  const workflowParam = searchParams.get('workflow');

  const workflowOptionsEnum = getWorkflowFromPolicyConfiguratorSection(policyConfiguratorSection);
  const workflowOptions = getEnumValues(workflowOptionsEnum);
  const initialWorkflowName = (workflowParam === null ? workflowOptions[0] : workflowParam);

  const [workflowName, setWorkflowName] = React.useState<string>(initialWorkflowName);
  const initialCompany = { companyName: '', companyId: '' };
  const [company, setCompany] = React.useState<ICompany>(initialCompany);
  const [conditions, setConditions] = React.useState<IRuleCondition[]>([emptyRuleCondition()]);
  const [isMarkingDynamicBehavior, setIsMarkingDynamicBehavior] = React.useState(false);
  const [outcomes, setOutcomes] = React.useState<IRuleOutcome[]>(initialOutcomesForWorkflow(workflowName));
  const [markingDynamicBehavior, setMarkingDBFormValues] = React.useState<IMarkingDynamicBehavior>({
    department: null,
    title: '',
    message: '',
  });
  const [isQuantity, setIsQuantity] = React.useState<boolean>(false);

  const { enqueueSnackbar } = useSnackbar();

  const concatMultilineMessage = (splitOutcome: Array<string>) => {
    let message = '';
    if (splitOutcome.length > 3) {
      for (let i = 3; i < splitOutcome.length; i += 1) {
        message += splitOutcome[i].toString();
      }
    }
    return message;
  };

  const ruleFetcher = (url: string, ruleName: string | null, workflowNameParam: string | null) => axios.get<IRuleDetail>(url, { params: { ruleName, workflowName: workflowNameParam } })
    .then((response) => {
      if (response.data.company) {
        setCompany({ ...response.data.company });
      }
      setConditions([...response.data.conditions]);
      setOutcomes([...response.data.outcomes]);
      setIsQuantity(response.data.isQuantity);

      if (workflowName === POLICYCONFIGURATORSECTIONS.MARKINGDYNAMICBEHAVIOR) {
        const splitOutcomes = response.data.outcomes[0].value.split(':');
        if (splitOutcomes.length > 3) {
          setMarkingDBFormValues({
            department: {
              value: 0,
              label: splitOutcomes[1],
            },
            title: splitOutcomes[2],
            message: concatMultilineMessage(splitOutcomes),
          });
          const updatedOutcomes = [...outcomes];
          updatedOutcomes[0].value = splitOutcomes[0].toString();
          setOutcomes(updatedOutcomes);
        }
      }

      return response.data;
    })
    .catch((error: AxiosError) => {
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
      enqueueSnackbar(`Error fetching rule data because of the following error : ${error?.response?.data}`, { variant: 'error' });
    });

  useSWR(
    (ruleNameParam && workflowParam)
      ? ['/rules/GetRule', ruleNameParam, workflowParam]
      : null,
    (url) => ruleFetcher(url, ruleNameParam, workflowParam),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  );

  const handleSave = () => {
    const errors: string[] = [];

    // Check company requirements
    const workflowRequiresCompany = companyRequiredWorkflowTypes.includes(outcomes[0].field);
    if (!company && workflowRequiresCompany) {
      enqueueSnackbar('No company is selected', { variant: 'error' });
      errors.push('No company selected');
    }

    // Check outcomes requirements
    const notEveryOutcomeComplete = outcomes.some((outcome) => Object.values(outcome).every((x) => x === null || x === ''));
    if (notEveryOutcomeComplete) {
      enqueueSnackbar('No outcome is selected', { variant: 'error' });
      errors.push('No outcome selected');
    }

    // Check outcomes requirements for dynamic behavior fields
    if (outcomes.every((x) => x.field === 'Marking Dynamic Behavior' && x.value === 'Manager Notification')) {
      const notEveryMarkingDynamicBehaviorFieldsComplete = (!markingDynamicBehavior?.department || markingDynamicBehavior?.title === '' || markingDynamicBehavior?.message === '');

      if (notEveryMarkingDynamicBehaviorFieldsComplete) {
        enqueueSnackbar('Required Marking Dynamic Behavior fields must not be empty or unselected.', { variant: 'error' });
        errors.push('No Marking Dynamic Behavior fields selected or inserted.');
      } else if (markingDynamicBehavior.department && markingDynamicBehavior.title && markingDynamicBehavior.message) {
        const markingDynamicBehaviorCombined = `${outcomes[0].value}:${markingDynamicBehavior?.department.label}:${markingDynamicBehavior?.title}:${markingDynamicBehavior?.message}`;
        const tempOutcomes = [...outcomes];
        tempOutcomes[0].value = markingDynamicBehaviorCombined;
        setOutcomes(tempOutcomes);
      }
    }

    // Check conditions requirements
    if (conditions.length === 0) {
      enqueueSnackbar('No conditions are selected', { variant: 'error' });
      errors.push('No conditions selected');
    }
    const notEveryConditionComplete = conditions.some((condition) => Object.values(condition).every((x) => x === null || x === ''));
    if (notEveryConditionComplete) {
      enqueueSnackbar('Some conditions are not completely filled out', { variant: 'error' });
      errors.push('Some conditions are incompletely filled out');
    }

    if (errors.length > 0) {
      setShowErrors(true);
    } else {
      setShowErrors(false);
      enqueueSnackbar('Saving...');

      const createRuleCommand: ICreateRuleCommand = {
        isEdit,
        ruleName: ruleNameParam || '',
        workflowName,
        company,
        conditions,
        outcomes,
        isQuantity,
      };

      axios.post('/rules/createRule', createRuleCommand)
        .then(() => {
          setShowUpsertRuleConfirmationDialog(true);
        })
        .catch((e: AxiosError) => {
          enqueueSnackbar(JSON.stringify(e?.response?.data), { persist: false, variant: 'error' });
        });
    }
  };

  const handleClearAll = () => {
    setCompany(emptyCompany());
    setConditions([emptyRuleCondition()]);
    setOutcomes([...initialOutcomesForWorkflow(workflowName)]);
    setIsMarkingDynamicBehavior(false);
    setMarkingDBFormValues(emptyMarkingDynamicBehaviorCondition());
  };

  const companyElementRef = React.useRef<HTMLInputElement>(null);

  const handleWorkflowTabChange = (event: unknown, newTabValue: string) => {
    setWorkflowName(newTabValue);
    setOutcomes([...initialOutcomesForWorkflow(newTabValue)]);
    // When changing tabs, company is still set even if the dropdown
    // hides... so we need to clear out the value before fetching non-company
    // related rules.
    setCompany(emptyCompany());
  };

  React.useEffect(() => {
    const splitOutcomes = outcomes[0].value.split(':');
    if (splitOutcomes.length > 3) {
      const updatedOutcomes = [...outcomes];
      updatedOutcomes[0].value = splitOutcomes[0].toString();
      setOutcomes(updatedOutcomes);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [window.location.pathname]);

  return (
    <Grid container>
      <Grid item lg={1} />
      <Grid item lg={9} md={12} xs={12}>
        <TabContext value={workflowName}>
          <Grid item xs={12}>
            <Alert severity="info">
              {`${isEdit ? 'Edit' : 'Create a new'} ${policyConfiguratorSection} Rule`}
            </Alert>
          </Grid>
          <Grid item xs={12}>
            <Box>
              <TabList
                onChange={handleWorkflowTabChange}
                variant="scrollable"
                scrollButtons="auto"
                indicatorColor="primary"
              >
                {workflowOptions.map((w) => (
                  <Tab label={w.replace('Destination - ', '')} value={w} />
                ))}
              </TabList>
            </Box>
          </Grid>
          <RuleFilters
            workflowName={workflowName}
            workflowType={policyConfiguratorSection}
            filterMode={isEdit ? 'Edit' : 'Create'}
            conditionFilterValues={conditions}
            setConditionFilterValues={setConditions}
            outcomeFilterValues={outcomes}
            setOutcomeFilterValues={setOutcomes}
            companyFilterValue={company}
            setCompanyFilterValue={setCompany}
            submitFilterValues={handleSave}
            showErrors={showErrors}
            companyRef={companyElementRef}
            handleClearAll={handleClearAll}
            markingDynamicBehavior={markingDynamicBehavior}
            setMarkingDBFormValues={setMarkingDBFormValues}
            isMarkingDynamicBehavior={isMarkingDynamicBehavior}
            setIsMarkingDynamicBehavior={setIsMarkingDynamicBehavior}
            isQuantity={isQuantity}
            setIsQuantity={setIsQuantity}
          />
          <UpsertRuleConfirmationDialog
            showModal={showUpsertRuleConfirmationDialog}
            setShowModal={setShowUpsertRuleConfirmationDialog}
            clearForm={handleClearAll}
            isEdit={isEdit}
            policyConfiguratorSection={policyConfiguratorSection}
            workflowName={workflowName}
            companyName={company ? company?.companyName : null}
          />
        </TabContext>
      </Grid>
    </Grid>
  );
};

export default UpsertRule;
